import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import EventIcon from '@material-ui/icons/Event';
import SaveIcon from '@material-ui/icons/Save';
import { toast } from 'react-toastify';

import api from '../../services/api';
import toastError from '../../errors/toastError';

/**
 * Criar/editar compromisso — layout alinhado a `QueueModal` (Departamentos):
 * cabeçalho com ícone + título + fechar, corpo #f8fafc, campos outlined, rodapé com borda.
 */
const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 600,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 48px)',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#e8f0fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 22, color: '#1d4ed8' },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    '&:hover': { backgroundColor: '#f1f5f9', color: '#334155' },
  },
  dialogContent: {
    padding: '20px 24px',
    backgroundColor: '#f8fafc',
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
    },
  },
  field: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': { borderRadius: 8 },
  },
  dialogActions: {
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e8edf2',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 36,
  },
  submitWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  btnProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -10,
    color: 'inherit',
  },
}));

const AppointmentModal = ({
  open,
  onClose,
  appointment,
  onSave,
  initialScheduleId,
  userServices,
  userConfig,
  existingAppointments,
}) => {
  const classes = useStyles();
  const isEdit = Boolean(appointment && appointment.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDatetime, setStartDatetime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [status, setStatus] = useState('scheduled');
  const [scheduleId, setScheduleId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [services, setServices] = useState([]);
  const [errors, setErrors] = useState({
    title: '',
    startDatetime: '',
    scheduleId: '',
    durationMinutes: '',
    scheduleValidation: '',
  });

  useEffect(() => {
    if (open) {
      loadData();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (open && appointment) {
      setTitle(appointment.title || '');
      setDescription(appointment.description || '');
      setDurationMinutes(String(appointment.durationMinutes || 60));
      setStatus(appointment.status || 'scheduled');
      setScheduleId(String(appointment.scheduleId || ''));
      setServiceId(appointment.serviceId ? String(appointment.serviceId) : '');

      if (appointment.startDatetime) {
        try {
          const date = new Date(appointment.startDatetime);
          const formatted = date.toISOString().slice(0, 16);
          setStartDatetime(formatted);
        } catch (e) {
          setStartDatetime('');
        }
      }
    }
  }, [open, appointment]);

  useEffect(() => {
    if (open && !appointment && initialScheduleId) {
      setScheduleId(String(initialScheduleId));
    }
  }, [open, appointment, initialScheduleId]);

  const loadData = async () => {
    try {
      const { data: schedulesRes } = await api.get('/user-schedules');
      setSchedules(schedulesRes.schedules || []);
    } catch (err) {
      console.error('Erro ao carregar agendas:', err);
    }

    if (userServices && userServices.length > 0) {
      setServices(userServices);
    } else {
      try {
        const { data: servicesRes } = await api.get('/servicos');
        setServices(servicesRes.servicos || servicesRes || []);
      } catch (err) {
        console.error('Erro ao carregar serviços:', err);
      }
    }
  };

  const resetForm = () => {
    if (!appointment) {
      setTitle('');
      setDescription('');
      setStartDatetime('');
      setDurationMinutes('60');
      setStatus('scheduled');
      setScheduleId(initialScheduleId ? String(initialScheduleId) : '');
      setServiceId('');
    }
    setErrors({
      title: '',
      startDatetime: '',
      scheduleId: '',
      durationMinutes: '',
      scheduleValidation: '',
    });
  };

  const clearError = (field) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateSchedule = () => {
    if (!startDatetime) return null;

    const start = new Date(startDatetime);
    const duration = parseInt(durationMinutes, 10) || 60;
    const end = new Date(start.getTime() + duration * 60000);

    if (userConfig) {
      const dayOfWeek = start.getDay();
      const workDaysArray = (userConfig.workDays || '0,1,2,3,4,5,6')
        .split(',')
        .map((d) => parseInt(d.trim(), 10));
      const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

      if (!workDaysArray.includes(dayOfWeek)) {
        return `O profissional não trabalha neste dia (${dayNames[dayOfWeek]}). Dias: ${workDaysArray.map((d) => dayNames[d]).join(', ')}`;
      }

      const startTime = start.toTimeString().substring(0, 5);
      const endTime = end.toTimeString().substring(0, 5);
      const userStartWork = userConfig.startWork || '00:00';
      const userEndWork = userConfig.endWork || '23:59';

      if (startTime < userStartWork || endTime > userEndWork) {
        return `Horário fora do expediente do profissional (${userStartWork} - ${userEndWork})`;
      }

      if (userConfig.lunchStart && userConfig.lunchEnd) {
        const lunchStartParts = userConfig.lunchStart.split(':');
        const lunchEndParts = userConfig.lunchEnd.split(':');
        const lunchStartMinutes =
          parseInt(lunchStartParts[0], 10) * 60 + parseInt(lunchStartParts[1], 10);
        const lunchEndMinutes =
          parseInt(lunchEndParts[0], 10) * 60 + parseInt(lunchEndParts[1], 10);

        const appointmentStartMinutes = start.getHours() * 60 + start.getMinutes();
        const appointmentEndMinutes = end.getHours() * 60 + end.getMinutes();

        const overlapsLunch =
          (appointmentStartMinutes >= lunchStartMinutes &&
            appointmentStartMinutes < lunchEndMinutes) ||
          (appointmentEndMinutes > lunchStartMinutes && appointmentEndMinutes <= lunchEndMinutes) ||
          (appointmentStartMinutes <= lunchStartMinutes &&
            appointmentEndMinutes >= lunchEndMinutes);

        if (overlapsLunch) {
          return `Conflito com horário de almoço (${userConfig.lunchStart} - ${userConfig.lunchEnd})`;
        }
      }
    }

    if (existingAppointments && existingAppointments.length > 0) {
      const newStart = start.getTime();
      const newEnd = end.getTime();

      for (const existing of existingAppointments) {
        if (appointment && existing.id === appointment.id) continue;
        if (existing.status === 'cancelled' || existing.status === 'no_show') continue;

        const existingStart = new Date(existing.startDatetime).getTime();
        const existingEnd = existingStart + existing.durationMinutes * 60000;

        if (
          (newStart >= existingStart && newStart < existingEnd) ||
          (newEnd > existingStart && newEnd <= existingEnd) ||
          (newStart <= existingStart && newEnd >= existingEnd)
        ) {
          const existingTime = new Date(existing.startDatetime).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          });
          return `Conflito com "${existing.title}" às ${existingTime}`;
        }
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    const durationNum = parseInt(durationMinutes, 10);
    const scheduleValidation = validateSchedule();

    const newErrors = {
      title: !title.trim() ? 'Título é obrigatório' : '',
      startDatetime: !startDatetime ? 'Data/hora de início é obrigatória' : '',
      scheduleId: !scheduleId ? 'Selecione uma agenda' : '',
      durationMinutes:
        !durationMinutes || isNaN(durationNum) || durationNum < 1
          ? 'Informe a duração em minutos (mín. 1)'
          : '',
      scheduleValidation: scheduleValidation || '',
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((v) => v !== '');
    if (hasError) return;

    setSubmitting(true);

    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        startDatetime,
        durationMinutes: parseInt(durationMinutes, 10) || 60,
        status,
        scheduleId: parseInt(scheduleId, 10),
        serviceId: serviceId ? parseInt(serviceId, 10) : null,
      };

      if (appointment && appointment.id) {
        await api.put(`/appointments/${appointment.id}`, payload);
        toast.success('Compromisso atualizado com sucesso');
      } else {
        await api.post('/appointments', payload);
        toast.success('Compromisso criado com sucesso');
      }

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const activeSchedules = schedules.filter((s) => s.active);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.paper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIconBox}>
            <EventIcon />
          </Box>
          <Box>
            <Typography className={classes.headerTitle} component="div">
              {isEdit ? 'Editar compromisso' : 'Novo compromisso'}
            </Typography>
            <Typography className={classes.headerSubtitle} component="div">
              {isEdit
                ? 'Atualize horário, serviço e status conforme necessário.'
                : 'Defina agenda, data e duração do atendimento.'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Fechar">
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Fechar"
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Título"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              className={classes.field}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                clearError('title');
              }}
              error={Boolean(errors.title)}
              helperText={errors.title}
              disabled={submitting}
              autoFocus
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              margin="dense"
              className={classes.field}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              placeholder="Opcional — observações sobre o compromisso"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Agenda"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              className={classes.field}
              value={scheduleId}
              onChange={(e) => {
                setScheduleId(e.target.value);
                clearError('scheduleId');
              }}
              error={Boolean(errors.scheduleId)}
              helperText={errors.scheduleId}
              disabled={Boolean(appointment) || submitting}
            >
              <MenuItem value="">
                <em>Selecione uma agenda</em>
              </MenuItem>
              {activeSchedules.map((schedule) => (
                <MenuItem key={schedule.id} value={String(schedule.id)}>
                  {schedule.name} {schedule.user ? `(${schedule.user.name})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Serviço (opcional)"
              fullWidth
              variant="outlined"
              margin="dense"
              className={classes.field}
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              disabled={submitting}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {services.map((service) => (
                <MenuItem key={service.id} value={String(service.id)}>
                  {service.nome} — R$ {Number(service.valorOriginal || 0).toFixed(2)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="datetime-local"
              label="Data/hora de início"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              className={classes.field}
              InputLabelProps={{ shrink: true }}
              value={startDatetime}
              onChange={(e) => {
                setStartDatetime(e.target.value);
                clearError('startDatetime');
                clearError('scheduleValidation');
              }}
              error={Boolean(errors.startDatetime || errors.scheduleValidation)}
              helperText={errors.startDatetime || errors.scheduleValidation}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              type="number"
              label="Duração (minutos)"
              fullWidth
              required
              variant="outlined"
              margin="dense"
              className={classes.field}
              inputProps={{ min: 1 }}
              value={durationMinutes}
              onChange={(e) => {
                setDurationMinutes(e.target.value);
                clearError('durationMinutes');
                clearError('scheduleValidation');
              }}
              error={Boolean(errors.durationMinutes)}
              helperText={errors.durationMinutes || 'Ex.: 60 = 1 hora'}
              disabled={submitting}
            />
          </Grid>

          {appointment && (
            <Grid item xs={12}>
              <TextField
                select
                label="Status"
                fullWidth
                variant="outlined"
                margin="dense"
                className={classes.field}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={submitting}
              >
                <MenuItem value="scheduled">Agendado</MenuItem>
                <MenuItem value="confirmed">Confirmado</MenuItem>
                <MenuItem value="completed">Concluído</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
                <MenuItem value="no_show">Não compareceu</MenuItem>
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          size="small"
          className={classes.btn}
          onClick={handleClose}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Box className={classes.submitWrap}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.btn}
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={<SaveIcon style={{ fontSize: 18 }} />}
          >
            {isEdit ? 'Salvar alterações' : 'Criar compromisso'}
          </Button>
          {submitting ? <CircularProgress size={20} className={classes.btnProgress} /> : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

AppointmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  onSave: PropTypes.func,
  initialScheduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userServices: PropTypes.array,
  userConfig: PropTypes.object,
  existingAppointments: PropTypes.array,
};

AppointmentModal.defaultProps = {
  appointment: null,
  onSave: undefined,
  initialScheduleId: '',
  userServices: undefined,
  userConfig: undefined,
  existingAppointments: undefined,
};

export default AppointmentModal;
